import { cn, Stack, Tooltip, TooltipContent, TooltipTrigger } from '@landler/component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { getNCChangeIcon } from '@/components/NCFactCapsule/NCFactCapsule';
import { ProgressBar } from '@/components/widgets/a/ProgressBar/ProgressBar';
import { TextContainer } from '@/components/widgets/a/Text/Text';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useFact } from '@/hooks/useFact';
import { FactCapsuleTrend } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { isNullish } from '@/utils/booleans';
import { formatFactKilogramsToTonnes } from '@/utils/formatting';

import { Icon } from '../../a/Icon/Icon';
import { TooltipContentWidget, TooltipTriggerWidget, TooltipWidget } from '../../m/Tooltip/Tooltip';

type ResilienceDriverProps = {
  maxFact?: PlotReportFactElement<number> | undefined;
  currentFact?: PlotReportFactElement<number> | undefined;
  trendFact?: PlotReportFactElement<number> | undefined;
  title?: string;
  styles?: Record<string, string>;
  tooltip?: {
    title?: string;
    description?: string;
  };
  onClick?: () => void;
  progressBarTooltip?: string;
};

export const ResilienceDriver = ({
  maxFact,
  currentFact,
  trendFact,
  title,
  tooltip,
  styles,
  onClick,
  progressBarTooltip,
  ...delegated
}: ResilienceDriverProps) => {
  const isMaxMissing = isNullish(maxFact);

  const showProgressBar = !isMaxMissing && currentFact;
  const showTitle = title && (trendFact || showProgressBar);
  const showTrend = trendFact && !isNullish(trendFact?.value);

  if (!showTitle && !showTrend && !showProgressBar) {
    return null;
  }

  return (
    <Stack
      className={cn(
        'w-full rounded-lg p-2',
        styles?.root,
        typeof onClick === 'function' ? 'hover:bg-neutral-black-4 hover:cursor-pointer' : '',
      )}
      spacing={3}
      onClick={onClick}
      {...delegated}
    >
      <Stack className='w-full justify-between' direction='row'>
        {showTitle && <ResilienceTitle title={title} tooltip={tooltip} />}
        {showTrend && <ResilienceTrend trendFact={trendFact} />}
      </Stack>
      {showProgressBar && (
        <ResilienceProgressBar
          title={title}
          maxFact={maxFact}
          currentFact={currentFact}
          progressBarTooltip={progressBarTooltip}
        />
      )}
    </Stack>
  );
};

const ResilienceTitle = ({ title, tooltip }: { title: string; tooltip?: { title?: string; description?: string } }) => {
  return (
    <Stack direction='row' spacing={2}>
      <TextContainer text={title} className='typography-body1' />
      {(tooltip?.description || tooltip?.title) && (
        <TooltipWidget>
          <TooltipTriggerWidget>
            <Icon icon='ri-information-line' />
          </TooltipTriggerWidget>
          <TooltipContentWidget>
            <Stack spacing={1}>
              {tooltip?.title && <TextContainer text={tooltip.title} className='typography-body2' />}
              {tooltip?.description && <TextContainer text={tooltip.description} className='typography-body3' />}
            </Stack>
          </TooltipContentWidget>
        </TooltipWidget>
      )}
    </Stack>
  );
};

const ResilienceProgressBar = ({
  title,
  maxFact,
  currentFact,
  progressBarTooltip,
}: {
  maxFact: PlotReportFactElement<number>;
  currentFact: PlotReportFactElement<number>;
  title?: string;
  progressBarTooltip?: string;
}) => {
  const { t } = useTranslation();

  const valueFact = useFact(formatFactKilogramsToTonnes(currentFact));
  const maximumFact = useFact(formatFactKilogramsToTonnes(maxFact));

  if (isNullish(currentFact?.value) || isNullish(maxFact?.value)) {
    return null;
  }
  const { value } = currentFact;
  const max = maxFact?.value;

  return (
    <Tooltip>
      <TooltipTrigger className='items-center'>
        <ProgressBar value={value} max={max} />
      </TooltipTrigger>
      <TooltipContent className='whitespace-pre-wrap'>
        {progressBarTooltip ??
          t('global.widget.resilienceDrivers.progressBarTooltip.body', {
            indicatorName: title ?? '',
            value: valueFact.display,
            max: maximumFact.display,
          })}
      </TooltipContent>
    </Tooltip>
  );
};

const ResilienceTrend = ({ trendFact }: { trendFact: PlotReportFactElement<number> }) => {
  const { t } = useTranslation();

  const displayTrend = parseFloat(getDisplayNumber(trendFact.value));

  if (displayTrend === 0) {
    return <span className='typography-body2Semibold'>0%</span>;
  }

  return (
    <Stack direction='row' className='w-fit' spacing={1} centerMain>
      <span className={`typography-body2Semibold ${trendFact.value > 0 ? 'text-success' : 'text-error'}`}>
        {getNCChangeIcon(trendFact)}
      </span>
      <FactCapsuleTrend trend={trendFact.value} />
      <Tooltip>
        <TooltipTrigger className='items-center'>
          <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
        </TooltipTrigger>
        <TooltipContent className='whitespace-pre-wrap'>{t('global.widget.historical1yUpliftTooltip')}</TooltipContent>
      </Tooltip>
    </Stack>
  );
};
